import React, { createContext, use, useEffect, ReactNode } from 'react';
import { useAuthStore, useIsAuthenticated } from '../../stores/auth-store';
import { userContextService } from '../../services/user-context.service';
import { useUserContextStore } from '../../stores/user-context.store';
import SessionTimeoutWarning from '../../components/SessionTimeoutWarning';
import { Loader } from '@tasama/ui';

interface AuthProviderProps {
  children: ReactNode;
  inactivityTimeout?: number;
}

// Create context for auth initialization status
const AuthContext = createContext<{ initialized: boolean }>({
  initialized: false,
});

/**
 * AuthProvider — initializes authentication state on every app load.
 *
 * With cookie-based auth the browser holds the session in an httpOnly cookie
 * that JS cannot read. We therefore cannot determine auth state from storage.
 * Instead, on every mount we call checkAuthStatus() which hits GET /auth/me:
 *   - 200 → valid cookie session, user object is populated, render children.
 *   - 401 → no valid session, state remains cleared, router redirects to login.
 *
 * React 19: uses <AuthContext> (no .Provider) and use() for context consumption
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  inactivityTimeout = 5 * 60 * 1000,
}) => {
  const [initialized, setInitialized] = React.useState(false);
  const { checkAuthStatus } = useAuthStore();
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Always ask the server — the httpOnly cookie is the source of truth.
        // checkAuthStatus() is a no-op if isAuthenticated is already true
        // (e.g. right after a login without a full page reload).
        await checkAuthStatus();
      } catch {
        // Errors are handled inside checkAuthStatus; nothing to do here
      } finally {
        setInitialized(true);
      }
    };

    initializeAuth();
  }, [checkAuthStatus]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (useUserContextStore.getState().userContext) return;

    userContextService
      .getUserContext(false)
      .then((response) => {
        if (response.status === 'success' && response.data) {
          useUserContextStore.getState().setUserContext(response.data);
        }
      })
      .catch(() => {});
  }, [isAuthenticated]);

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader size="lg" testID="auth-provider-loader" />
      </div>
    );
  }

  return (
    <AuthContext value={{ initialized }}>
      {children}
      <SessionTimeoutWarning
        testID="app-session-timeout-warning"
        inactivityTimeout={inactivityTimeout}
      />
    </AuthContext>
  );
};

export const useAuthContext = () => {
  const context = use(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
